import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { PATH } from '~/constants/route-path';
import { useInitializeIntercom } from '~/hooks/useInitializeIntercom';
import { useAppDispatch } from '~/hooks/useRedux';
import { cookieService } from '~/services/cookie-service';
import { googleAnalytics } from '~/services/google-analytics-service';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { getSubscriptionInfoThunk } from '~/store/reducers/account/slice';
import { useErrorsStore } from '~/store/useErrorStore';
import { getInitialRoute, logOut } from '~/utils/common';

import SettingsMobileModal from '../header/components/SettingsMobileModal';

import { Sidebar } from './components/Sidebar/Sidebar';
import { isSidebarVisible } from './utils';

function Layout({ children }: PropsWithChildren<{}>) {
  const { data, isSubscriptionValid } = useSelector(getLoggedInUser);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isOn404 } = useErrorsStore();

  const [openMobileSup, setMobileSup] = useState(false);

  const isAccessCookie = cookieService.getCookie('access') || null;

  const initialRoute = useMemo(
    () => getInitialRoute({ isAuthenticated: !!isAccessCookie, pathname: location.pathname }),
    [isAccessCookie, location.pathname],
  );

  useInitializeIntercom();

  // handle redirect when payment
  useEffect(() => {
    if (isAccessCookie) {
      dispatch(getSubscriptionInfoThunk());

      if (!isSubscriptionValid) {
        const allowedRoutes = [PATH.QR.MAIN, PATH.PAYMENTS.MAIN];

        if (!allowedRoutes.includes(location.pathname) && !location.pathname.includes('/s/')) {
          navigate(PATH.PAYMENTS.MAIN);
        }
      }
    }
  }, [isSubscriptionValid, isAccessCookie]);

  const handleSettingsClick = () => {
    navigate(PATH.SETTINGS);
    setMobileSup(false);
  };

  const handleLogOutClick = async () => {
    await logOut();
    googleAnalytics.events.logOut();
    setMobileSup(false);
  };

  if (initialRoute && initialRoute !== location.pathname) {
    // TODO not sure we need this
    // return <Navigate to={initialRoute} />;
  }

  return (
    <>
      {isSidebarVisible(location.pathname) && !isOn404 ? <Sidebar>{children}</Sidebar> : { ...children }}
      <SettingsMobileModal
        open={openMobileSup}
        setOpen={setMobileSup}
        user={{ email: data.email || '' }}
        onSettingsClick={handleSettingsClick}
        onLogOutClick={handleLogOutClick}
      />
    </>
  );
}

export default Layout;
